import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import Preloader, { InlineLoader, SpinnerLoader } from "../Preloader";
import { AdminsRoutes } from "../../routes";

const astFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Port_of_Spain",
  month: "numeric",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  second: "2-digit",
  hour12: true,
});

const formatToAST = (date) => {
  if (!date) return null;

  const d = new Date(date);

  if (isNaN(d.getTime())) return null;

  return astFormatter.format(d);
};


const NotificationPopup = ({
  isOpen,
  onClose,
  title,
  targetElement,
  loading,
  data = [],
  hasUnreadNotifications,
  unreadCount,
}) => {
  const popupRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target) &&
        targetElement &&
        !targetElement.contains(event.target)
      ) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose, targetElement]);

  if (!isOpen) return null;

  const getPopupPosition = () => {
    if (!targetElement) return { top: 60, right: 20 };
    const rect = targetElement.getBoundingClientRect();
    const isSmallScreen = window.innerWidth <= 375;
  
    return {
      top: rect.bottom + 10,
      right: isSmallScreen ? 10 : window.innerWidth - rect.right,
    };
  };
  

  const position = getPopupPosition();

  // Function to get redirect URL based on notification type
  const getRedirectUrl = (notification) => {
    switch (notification.notificationType) {
      case "Withdrawal":
        return AdminsRoutes.TransactionsBankingWithdrawals;
      case "Deposit":
        return AdminsRoutes.TransactionsBankingDeposits;
      case "User":
      case "UserSignup":
        return AdminsRoutes.Users;
      case "Bonus":
        return AdminsRoutes.Bonus;
      case "Game":
        return AdminsRoutes.Games;
      case "Support":
        return AdminsRoutes.Support;
      default:
        return AdminsRoutes.Dashboard;
    }
  };

  const getNotificationTitleColor = (type) => {
    switch (type) {
      case "Withdrawal":
        return "#dc3545";
      case "Deposit":
        return "#28a745";
      default:
        return "#000";
    }
  };

  const popupContent = (
    <div
      ref={popupRef}
      style={{
        position: "fixed",
        top: `${position.top}px`,
        right: `${position.right}px`,
        background: "white",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
        zIndex: 10000,
       
        minWidth: window.innerWidth <= 375 ? "95%" : "320px",
        maxWidth: "400px",
        maxHeight: "500px",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "16px 20px",
          borderBottom: "1px solid #e0e0e0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "#f8f9fa",
        }}
      >
        <h5 style={{ margin: 0, fontSize: "16px", fontWeight: "600" }}>
          {title}
          {unreadCount > 0 && (
            <span
              style={{
                marginLeft: "8px",
                background: "#dc3545",
                color: "white",
                borderRadius: "12px",
                padding: "2px 8px",
                fontSize: "12px",
              }}
            >
              {unreadCount}
            </span>
          )}
        </h5>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            fontSize: "20px",
            cursor: "pointer",
            padding: "0",
            width: "24px",
            height: "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          ×
        </button>
      </div>

      {/* Content */}
      <div style={{ maxHeight: "400px", overflowY: "auto" }}>
        {loading ? (
          <div style={{ padding: "40px", textAlign: "center" }}>
            <InlineLoader />
          </div>
        ) : data.length === 0 ? (
          <div style={{ padding: "40px", textAlign: "center", color: "#666" }}>
            No notifications found
          </div>
        ) : (
          <div>
            {data.map((notification, index) => {
              const astTime = formatToAST(
                notification.createdAt ||
                notification.created_at ||
                notification.timestamp
              );
              let message = notification?.notificationMessage || notification?.message || "";

             if (astTime) {
                message = message.replace(
                  /\d{1,2}\/\d{1,2}\/\d{4},?\s+\d{1,2}:\d{2}:\d{2}\s*(AM|PM)?\.?/i,
                  astTime
                );

                if (
                  notification?.notificationType === "Withdrawal" ||
                  notification?.notificationType === "Deposit"
                ) {
                  message = message
                    .split(" ")
                    .reduce((acc, curr, i) => {
                      if (i === 0 || i === 4) {
                        return `${acc} <span style="color:#00133c;font-weight:bold;">${curr}</span>`;
                      }
                      return `${acc} ${curr}`;
                    }, "");
                }
              }

              const redirectTo = getRedirectUrl(notification);

              return (
                <Link
                  key={notification.id || index}
                  to={redirectTo}
                  onClick={onClose} // Close popup when clicking on notification
                  style={{
                    textDecoration: "none",
                    color: "inherit",
                    display: "block",
                  }}
                >
                  <div
                    style={{
                      padding: "16px 20px",
                      borderBottom:
                        index < data.length - 1 ? "1px solid #f0f0f0" : "none",
                      background: notification.isRead ? "white" : "#f8f9ff",
                      cursor: "pointer",
                      transition: "background-color 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "#f5f5f5";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = notification.isRead
                        ? "white"
                        : "#f8f9ff";
                    }}
                  >
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "500",
                        marginBottom: "4px",
                      }}
                    >
                      <span
                        style={{
                          color: getNotificationTitleColor(notification?.notificationType),
                          fontWeight: "bold",
                        }}
                      >
                        {notification?.notificationType || "Notification"}
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#666",
                        marginBottom: "8px",
                      }}
                      dangerouslySetInnerHTML={{
                        __html:
                          message ||
                          notification?.notificationMessage ||
                          notification?.message ||
                          "No message available",
                      }}
                    />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(popupContent, document.body);
};

export default NotificationPopup;
